import { useMessagedAsyncRetry } from "@payping/web-components";
import PayGetOrderUnAvailable from "@/pages/Pay/GetOrder/UnAvailable";
import { APP_ROUTES, useAppDispatch, useAppSelector } from "@/utils";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { bnplConsumerGetOrder, bnplConsumerGetProfile } from "@payping/redux/actions";
import Loading from "@/pages/loading";
import { OrderSteps } from "@payping/api/app/api/bnplConsumerTypes";
import { dashboardPersistReduxActions } from "@/redux/actions/dashboardPersist";
import { RetryGetOrderSteps } from "@/redux/reducers/dashboard";
import { dashboardReduxReducerActions } from "@/redux/actions/dashboard";
import PayGetOrderCreditScore from "./CreditScore";
import PayGetOrderProfile from "./Profile";
import IssuePromissory from "./IssuePromissory";

import UploadSignature from "./UploadSignature";
import UploadVideoAndSignature from "./uploadVideoAndSignature";
import PayGetOrderPrePay from "./PrePay";
import PayUnAvailable from "../Unavailable";
import PayExpiredOrder from "../ExpiredOrder";
import PayOrderCancelled from "../PayOrderCancelled";
import PayGetOrderLayout from "./Layout";
import GetOrderUploadVideoAndSignatureLayout from "./uploadVideoAndSignature/Layout";
import Contract from "./Contract";
import GetOrderCheckInquiry from "./cheque-inquiry";
import GetOrderChequeSubmit from "./cheque-submit";

const PayGetOrder = () => {
  const [searchParams] = useSearchParams();
  const trackingCode = searchParams.get("trackingCode") || "";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [visiblePage, setVisiblePage] = useState<OrderSteps | null>(null);
  const retryGetOrder = useAppSelector((state) => state.dashboard.retryGetOrder);
  const order = useAppSelector((state) => state.bnplConsumer.order);

  const getProfileState = useMessagedAsyncRetry(
    async () => {
      try {
        const res = await dispatch(bnplConsumerGetProfile({ isRaw: true }));

        return res;
      } catch (e: any) {
        // Check if e is an instance of Response and if the status is 400
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        // Handle any other errors
        throw e;
      }
    },
    [dispatch],
    { success: false, error: true },
  );

  const {
    loading: getOrderLoading,
    error,
    retry,
  } = useMessagedAsyncRetry(
    async () => {
      try {
        const res = await dispatch(bnplConsumerGetOrder(trackingCode, { isRaw: true }));
        if (res.nextStep) {
          setVisiblePage(res.nextStep as unknown as OrderSteps);
        }
        dispatch(dashboardPersistReduxActions.setShopInfo(res.merchantName ?? "", res.merchantRedirectUrl ?? ""));
        if (retryGetOrder === RetryGetOrderSteps.endGetOrder && res.nextStep) {
          setVisiblePage(res.nextStep as unknown as OrderSteps);
        }
        if (retryGetOrder === RetryGetOrderSteps.endGetOrder && !res.nextStep) {
          setVisiblePage(null);
        }
        dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.disable));
        return res;
      } catch (e: any) {
        // Check if e is an instance of Response and if the status is 400
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        // Handle any other errors
        throw e;
      }
    },
    [trackingCode, retryGetOrder, dispatch],
    { success: false, error: true },
  );

  useEffect(() => {
    if (!trackingCode) {
      navigate(APP_ROUTES.home.overview.url);
    } else {
      dispatch(dashboardPersistReduxActions.setTrackingCode(trackingCode));
    }
  }, [dispatch, navigate, trackingCode]);

  useEffect(() => {
    // if order is completed
    if ((order.nextStep as unknown as OrderSteps) === OrderSteps.GetInstallments) {
      navigate(APP_ROUTES.home.orderDetail.url.replace(":code", trackingCode));
    }

    if (
      (order.nextStep as unknown as OrderSteps) === OrderSteps.PaidOff ||
      (order.nextStep as unknown as OrderSteps) === OrderSteps.Canceled
    ) {
      navigate(APP_ROUTES.home.overview.url);
    }
  }, [navigate, order, trackingCode]);

  useEffect(() => {
    if (retryGetOrder === RetryGetOrderSteps.startGetOrder) {
      retry();
      dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.endGetOrder));
    }
  }, [dispatch, retry, retryGetOrder]);

  // keep visiblePage in sync with order.nextStep from redux, adjusted during render instead of a cascading effect render
  const orderNextStep = order.nextStep as unknown as OrderSteps | undefined;
  if (visiblePage && orderNextStep && visiblePage !== orderNextStep) {
    setVisiblePage(orderNextStep);
  }

  if (error?.name === "ServerError") {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            retry();
          }}
          onRetryLoading={getOrderLoading}
        />
      </PayGetOrderLayout>
    );
  }

  if (getProfileState.error?.name === "ServerError") {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            getProfileState.retry();
          }}
          onRetryLoading={getOrderLoading}
        />
      </PayGetOrderLayout>
    );
  }

  if ((error as any)?.key === 109) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayExpiredOrder />
      </PayGetOrderLayout>
    );
  }

  if ((error as any)?.status === 400) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayGetOrderUnAvailable
          name={order.merchantName}
          url={order.merchantRedirectUrl}
          errorMessages={(error as any)?.errors ?? []}
        />
      </PayGetOrderLayout>
    );
  }

  if ((getProfileState.error as any)?.status === 400) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayGetOrderUnAvailable
          name={order.merchantName}
          url={order.merchantRedirectUrl}
          errorMessages={(error as any)?.errors ?? []}
        />
      </PayGetOrderLayout>
    );
  }

  if (
    getOrderLoading ||
    getProfileState.loading ||
    error?.name === "AuthError" ||
    getProfileState.error?.name === "AuthError"
  ) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <Loading />
      </PayGetOrderLayout>
    );
  }

  // cancelled order
  if ((order.nextStep as unknown as OrderSteps) === OrderSteps.Canceled) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayOrderCancelled />
      </PayGetOrderLayout>
    );
  }

  if (visiblePage === OrderSteps.CreditScore) {
    return (
      <PayGetOrderCreditScore
        trackingCode={trackingCode}
        afterSuccess={() => {
          retry();
        }}
      />
    );
  }

  if (visiblePage === OrderSteps.FillProfile) {
    return (
      <PayGetOrderProfile
        afterSuccess={() => {
          retry();
        }}
        trackingCode={trackingCode}
      />
    );
  }

  if (visiblePage === OrderSteps.IssuePromissory) {
    return (
      <IssuePromissory
        afterSuccess={() => {
          retry();
        }}
        trackingCode={trackingCode}
      />
    );
  }
  if (visiblePage === OrderSteps.UploadSignature) {
    return (
      <UploadSignature
        afterSuccess={() => {
          retry();
        }}
        trackingCode={trackingCode}
      />
    );
  }

  if (visiblePage === OrderSteps.ChequeSubmit) {
    return (
      <GetOrderChequeSubmit
        afterSuccess={() => {
          retry();
        }}
        trackingCode={trackingCode}
      />
    );
  }

  if (visiblePage === OrderSteps.ChequeInquiry) {
    return (
      <GetOrderCheckInquiry
        afterSuccess={() => {
          retry();
        }}
        trackingCode={trackingCode}
      />
    );
  }

  if (visiblePage === OrderSteps.SignContract) {
    return (
      <Contract
        trackingCode={trackingCode}
        afterSuccess={() => {
          retry();
        }}
      />
    );
  }

  if (visiblePage === OrderSteps.UploadVideoAndSignature) {
    return (
      <GetOrderUploadVideoAndSignatureLayout>
        <UploadVideoAndSignature
          afterSuccess={() => {
            retry();
          }}
          trackingCode={trackingCode}
        />
      </GetOrderUploadVideoAndSignatureLayout>
    );
  }

  if (visiblePage === OrderSteps.Quote) {
    return (
      <PayGetOrderPrePay
        afterSuccess={() => {
          retry();
        }}
        trackingCode={trackingCode}
      />
    );
  }

  return null;
};

export default PayGetOrder;
